import { create } from "zustand";

import { logger } from "@/telemetry";

/**
 * Tool Preferences Store
 *
 * Manages per-user tool selection preferences with localStorage persistence.
 * Stores tool selections keyed by workflow ID and agent ID, so preferences
 * survive page refreshes without modifying the shared workflow definition.
 */

const STORAGE_KEY = "tool-preferences";

type ToolPreferences = Record<string, Record<string, string[]>>;

export interface ToolPreferencesState {
  /** { workflowId: { agentId: ["tool_id_1", "tool_id_2"] } } */
  preferences: ToolPreferences;

  /** Save tool IDs for a specific workflow agent. Persists to localStorage. */
  setAgentTools: (workflowId: string, agentId: string, toolIds: string[]) => void;

  /** Get stored tool IDs for a specific workflow agent, or undefined if none saved. */
  getAgentTools: (workflowId: string, agentId: string) => string[] | undefined;

  /** Check whether any preferences exist for a given workflow. */
  hasPreferences: (workflowId: string) => boolean;

  /** Remove all stored preferences for a workflow. */
  clearWorkflowPreferences: (workflowId: string) => void;

  /** Merge backend preferences into the store. localStorage values win on conflict. */
  loadFromBackend: (backendPrefs: ToolPreferences) => void;

  /** Return current preferences for backend sync. */
  getPreferencesForSync: () => ToolPreferences;
}

/** Validate that a parsed value conforms to Record<string, Record<string, string[]>>. */
function isValidToolPreferences(value: unknown): value is ToolPreferences {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  for (const agents of Object.values(value as Record<string, unknown>)) {
    if (!agents || typeof agents !== "object" || Array.isArray(agents)) return false;

    for (const tools of Object.values(agents as Record<string, unknown>)) {
      if (!Array.isArray(tools)) return false;
      if (!tools.every((t) => typeof t === "string")) return false;
    }
  }

  return true;
}

function readFromLocalStorage(): ToolPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored) {
      const parsed = JSON.parse(stored);

      if (isValidToolPreferences(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Corrupted or unavailable localStorage — start fresh
  }

  return {};
}

function writeToLocalStorage(preferences: ToolPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    logger.warn("Failed to save tool preferences to localStorage", {
      error: String(error),
    });
  }
}

export const useToolPreferencesStore = create<ToolPreferencesState>((set, get) => ({
  preferences: readFromLocalStorage(),

  setAgentTools: (workflowId: string, agentId: string, toolIds: string[]) => {
    const current = get().preferences;
    const updated: ToolPreferences = {
      ...current,
      [workflowId]: {
        ...current[workflowId],
        [agentId]: toolIds,
      },
    };

    set({ preferences: updated });
    writeToLocalStorage(updated);
  },

  getAgentTools: (workflowId: string, agentId: string) => {
    return get().preferences[workflowId]?.[agentId];
  },

  hasPreferences: (workflowId: string) => {
    return workflowId in get().preferences;
  },

  clearWorkflowPreferences: (workflowId: string) => {
    const current = get().preferences;
    const { [workflowId]: _removed, ...remaining } = current;

    set({ preferences: remaining });
    writeToLocalStorage(remaining);
  },

  loadFromBackend: (backendPrefs: ToolPreferences) => {
    const local = get().preferences;

    // Merge: localStorage wins on conflict (more recent), backend fills gaps
    const merged: ToolPreferences = { ...backendPrefs };

    for (const [workflowId, agents] of Object.entries(local)) {
      if (!merged[workflowId]) {
        merged[workflowId] = agents;
      } else {
        merged[workflowId] = { ...merged[workflowId] };

        for (const [agentId, tools] of Object.entries(agents)) {
          // localStorage wins — it represents the most recent user action on this device
          merged[workflowId][agentId] = tools;
        }
      }
    }

    set({ preferences: merged });
    writeToLocalStorage(merged);
  },

  getPreferencesForSync: () => {
    return get().preferences;
  },
}));
